import type { MouseEvent, ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { cn, tw } from '@crm/shared/lib';

import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from './PaginationPrimitives';
import { usePagination } from './usePagination';

interface PaginationData {
    limit: number;
    page: number;
    total: number;
}

interface IListPaginationProps {
    onPageChange: (targetPage: number) => void;
    onPageNormalize: (targetPage: number) => void;
    pagination?: PaginationData;
    summary?: ReactNode;
}

type TListPaginationContentProps = PaginationData &
    Pick<IListPaginationProps, 'onPageChange' | 'onPageNormalize' | 'summary'> & {
        isPending?: boolean;
    };

const ListPaginationContent = ({
    isPending = false,
    total,
    limit,
    onPageChange,
    onPageNormalize,
    page,
    summary,
}: TListPaginationContentProps) => {
    const { t } = useTranslation();
    const paginationBtnStyles = tw('text-smtr-main-tertiary-100');
    const disabledPaginationBtnStyles = tw('pointer-events-none opacity-50');
    const {
        changePage,
        currentPage,
        hasNextEllipsis,
        hasNextPage,
        hasPreviousEllipsis,
        hasPreviousPage,
        totalPages,
        visiblePages,
    } = usePagination({ limit, onPageChange, onPageNormalize, page, total });

    if (totalPages === 0) {
        return null;
    }

    const onPaginationClick = (event: MouseEvent<HTMLAnchorElement>, targetPage: number) => {
        event.preventDefault();
        changePage(targetPage);
    };

    return (
        <div
            aria-hidden={isPending || undefined}
            className={cn('relative w-full', isPending && 'invisible')}
        >
            {summary && (
                <span className="absolute top-1/2 left-0 -translate-y-1/2 text-xs text-smtr-main-tertiary-100">
                    {summary}
                </span>
            )}
            <Pagination>
                <PaginationContent>
                    <PaginationItem>
                        <PaginationPrevious
                            href="#"
                            aria-disabled={!hasPreviousPage}
                            tabIndex={hasPreviousPage ? undefined : -1}
                            className={`${paginationBtnStyles} ${
                                hasPreviousPage ? '' : disabledPaginationBtnStyles
                            }`}
                            onClick={(event) => onPaginationClick(event, currentPage - 1)}
                        />
                    </PaginationItem>
                    {hasPreviousEllipsis && (
                        <PaginationItem>
                            <PaginationEllipsis />
                        </PaginationItem>
                    )}
                    {visiblePages.map((visiblePage) => (
                        <PaginationItem key={visiblePage}>
                            <PaginationLink
                                href="#"
                                isActive={visiblePage === currentPage}
                                aria-label={t('crm.common.pagination.pageAriaLabel', {
                                    page: visiblePage,
                                })}
                                onClick={(event) => onPaginationClick(event, visiblePage)}
                            >
                                {visiblePage}
                            </PaginationLink>
                        </PaginationItem>
                    ))}
                    {hasNextEllipsis && (
                        <PaginationItem>
                            <PaginationEllipsis />
                        </PaginationItem>
                    )}
                    <PaginationItem>
                        <PaginationNext
                            href="#"
                            aria-disabled={!hasNextPage}
                            tabIndex={hasNextPage ? undefined : -1}
                            className={`${paginationBtnStyles} ${
                                hasNextPage ? '' : disabledPaginationBtnStyles
                            }`}
                            onClick={(event) => onPaginationClick(event, currentPage + 1)}
                        />
                    </PaginationItem>
                </PaginationContent>
            </Pagination>
        </div>
    );
};

export const ListPagination = ({
    onPageChange,
    onPageNormalize,
    pagination,
    summary,
}: IListPaginationProps) => (
    <ListPaginationContent
        isPending={!pagination}
        limit={pagination?.limit ?? 1}
        page={pagination?.page ?? 1}
        total={pagination?.total ?? 1}
        onPageChange={onPageChange}
        onPageNormalize={onPageNormalize}
        summary={summary}
    />
);
